import {PermissionType} from '@hconnect/apiclient'
import {
  EquipmentRunningTimes,
  EquipmentType,
  GetStackedBarChartComponent,
  RunningTimesEquipmentData,
  RunningTimeType,
  DowntimeStoppageCodes
} from '@hconnect/common/types'
import {FiberManualRecord as Circle} from '@mui/icons-material'
import {Box, Divider, Grid, Theme, Typography} from '@mui/material'
import {TFunction} from 'i18next'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {MissingStoppageAlert, RunningTimesView} from '../components'
import {RUNNING_TIMES_WIDGET_NAMESPACE} from '../consts'
import {useDefaultStoppageData} from '../hooks'
import {getRunningTimeIndex, getUndefinedStoppages, isKilnRunning} from '../utils'

import {UndefinedStoppagesDialog} from './UndefinedStoppagesDialog'

const statusStyle = {display: 'flex', alignItems: 'center'}

const getStatusIndicator = (t: TFunction, activeStatus?: RunningTimeType) => {
  if (!activeStatus) return null
  const running = isKilnRunning(activeStatus)
  const colorKey = running ? 'success' : 'error'
  const label = t(running ? 'runningTime.label.running' : 'runningTime.label.off')

  return (
    <Typography
      mr={2}
      variant="caption"
      sx={(theme: Theme) => ({
        ...statusStyle,
        color: theme.palette[colorKey].light
      })}
    >
      <Circle
        sx={(theme: Theme) => ({
          color: theme.palette[colorKey].light,
          verticalAlign: 'text-bottom'
        })}
      />
      {label}
    </Typography>
  )
}

type Props = {
  equipment: RunningTimesEquipmentData
  runningTimePerformance?: EquipmentRunningTimes
  hideDivider: boolean
  getStackedBarChartComponent?: GetStackedBarChartComponent
  activeStatus?: RunningTimeType
  onClick?: () => void
  onStoppageCreationSuccess: () => void
  plantId: string
  stoppageCodes: DowntimeStoppageCodes
  hasPermission: (permission: PermissionType) => boolean
  plantTimeZone: string
}

export const EquipmentRunningTimesResult: React.FC<Props> = ({
  onStoppageCreationSuccess,
  equipment,
  hideDivider,
  getStackedBarChartComponent,
  runningTimePerformance,
  activeStatus,
  plantId,
  onClick,
  stoppageCodes,
  hasPermission,
  plantTimeZone
}) => {
  const {t} = useTranslation(RUNNING_TIMES_WIDGET_NAMESPACE)
  const [isUndefinedStoppageOpen, setIsUndefinedStoppageOpen] = useState(false)
  const [item, setItem, isLoading] = useDefaultStoppageData({
    runningTimePerformance,
    equipment,
    plantId,
    enabled: isUndefinedStoppageOpen
  })

  // Count the number of stoppages that are undefined - that need to be filled in
  const stoppagesCount = React.useMemo(
    () => getUndefinedStoppages(runningTimePerformance?.runningTimes)?.length ?? 0,
    [runningTimePerformance]
  )

  const isKiln = equipment.equipmentType === EquipmentType.KILN

  const currentUndefinedRunningTimes = React.useMemo(
    () =>
      isUndefinedStoppageOpen
        ? getUndefinedStoppages(runningTimePerformance?.runningTimes)
        : undefined,
    [runningTimePerformance, isUndefinedStoppageOpen]
  )

  const currentIndex = isUndefinedStoppageOpen
    ? getRunningTimeIndex(currentUndefinedRunningTimes, item)
    : undefined
  const highlightedRunningTimeId =
    currentIndex !== undefined ? currentUndefinedRunningTimes?.[currentIndex]?.id : undefined

  return (
    <>
      {isKiln && isUndefinedStoppageOpen && (
        <UndefinedStoppagesDialog
          isLoading={isLoading}
          hasPermission={hasPermission}
          plantTimeZone={plantTimeZone}
          onClose={() => setIsUndefinedStoppageOpen(false)}
          setItem={setItem}
          equipment={equipment}
          currentUndefinedRunningTimes={currentUndefinedRunningTimes}
          currentIndex={currentIndex}
          stoppagesCount={stoppagesCount}
          item={item}
          plantId={plantId}
          stoppageCodes={stoppageCodes}
          onStoppageCreationSuccess={onStoppageCreationSuccess}
        >
          <Grid
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 1,
              padding: 2,
              marginBottom: 2
            }}
          >
            <Box width="100%">
              <RunningTimesView
                equipment={equipment}
                runningTimePerformance={runningTimePerformance}
                getStackedBarChartComponent={
                  runningTimePerformance &&
                  getStackedBarChartComponent?.({
                    equipment: equipment,
                    runningTimes:
                      runningTimePerformance?.runningTimes?.filter((rt) => rt.begin !== rt.end) ||
                      [],
                    onUndefinedStoppageClick: setItem,
                    highlightedRunningTimeId
                  })
                }
              />
            </Box>
          </Grid>
        </UndefinedStoppagesDialog>
      )}

      <Grid
        data-test-id={`equipment-rt-${equipment.id}`}
        sx={[!!onClick && {cursor: 'pointer'}]}
        onClick={onClick}
      >
        <Box display="flex" alignItems="center" width="100%" py={1.5}>
          <Box width="100%">
            {isKiln && stoppagesCount > 0 && (
              <MissingStoppageAlert
                stoppagesCount={stoppagesCount}
                handleOpen={() => setIsUndefinedStoppageOpen(true)}
              />
            )}

            <RunningTimesView
              equipment={equipment}
              runningTimePerformance={runningTimePerformance}
              getStackedBarChartComponent={
                !runningTimePerformance
                  ? null
                  : getStackedBarChartComponent?.({
                      equipment: equipment,
                      runningTimes:
                        runningTimePerformance?.runningTimes?.filter((rt) => rt.begin !== rt.end) ||
                        []
                    })
              }
              afterTitleComponent={getStatusIndicator(t, activeStatus)}
            />
          </Box>
        </Box>

        {!hideDivider && <Divider color="light" />}
      </Grid>
    </>
  )
}
